import { Box, List, TableCell, TableCellProps } from "@mui/material";
import { ReactNode } from "react";

import InfoMessage from "../info-message";
import { StyledListItem, StyledTypography } from "../styled-components";

export const NOT_PROVIDED = <i>Not Provided</i>;
export const INCONSISTENT_DATA = "inconsistentData";

type HeaderWeight = "bold" | "normal";

type Keyed<T> = { key: string; value: T };

/**
 * Keys a positional list by its rendered text, suffixing repeats so two equal
 * cells in one row still get distinct keys without falling back to the index.
 */
// Positional cells have no id, and equal values in one row are possible, so the
// key pairs the text with its occurrence count in an encoding no text can forge.
export const keyedByText = <T,>(
  values: readonly T[],
  toText: (value: T) => string,
): Keyed<T>[] =>
  values.map((value, i) => {
    const text = toText(value);
    const repeats = values.slice(0, i).filter((v) => toText(v) === text).length;
    return { key: JSON.stringify([text, repeats]), value };
  });

const cellText = (value: string | null): string => value ?? "null";
const cellsText = (values: (string | null)[]): string =>
  values.map(cellText).join("|");

const HeaderText = ({
  children,
  fontWeight,
}: {
  children: ReactNode;
  fontWeight: HeaderWeight;
}) => (
  <StyledTypography align="center">
    {fontWeight === "bold" ? <strong>{children}</strong> : children}
  </StyledTypography>
);

const TextFieldWithTooltip = ({
  children = null,
  text = "",
  tooltipMsg = "",
}) => (
  <StyledListItem justifyContent="center">
    <InfoMessage tooltipMsg={tooltipMsg}>{children ?? text}</InfoMessage>
  </StyledListItem>
);

export const InconsistentDataCell = (): React.JSX.Element => (
  <TextFieldWithTooltip
    text="-"
    tooltipMsg="Not able to be displayed due to missing or inconsistent data"
  />
);

const DisplayValue = ({ value }: { value: string | null }) => {
  if (value === INCONSISTENT_DATA) {
    return <InconsistentDataCell />;
  }
  return (
    <StyledTypography align="center" whiteSpace="pre-wrap">
      {value || NOT_PROVIDED}
    </StyledTypography>
  );
};

type CoverageCellProps = {
  value: string | null;
  colSpan?: number;
  customRender?: React.ReactNode;
};

const CoverageCell = ({
  value,
  colSpan = 1,
  customRender,
}: CoverageCellProps): React.JSX.Element => (
  <TableCell colSpan={colSpan}>
    {customRender ?? <DisplayValue value={value || ""} />}
  </TableCell>
);

type CoverageTitleCellProps = {
  label: string;
};

const CoverageTitleCell = ({
  label,
}: CoverageTitleCellProps): React.JSX.Element => (
  <TableCell>
    <StyledTypography align="left">{label}</StyledTypography>
  </TableCell>
);

type CoverageVectorCellProps = {
  value: (string | null)[];
  colSpan?: number;
};

const CoverageVectorCell = ({
  value,
  colSpan = 1,
}: CoverageVectorCellProps): React.JSX.Element => (
  <TableCell colSpan={colSpan}>
    <Box display="flex" gap={2} alignItems="center">
      {keyedByText(value, cellText).map((c) => (
        <Box key={c.key} flex={1}>
          <DisplayValue value={c.value} />
        </Box>
      ))}
    </Box>
  </TableCell>
);

type CoverageMultiVectorCellProps = {
  value: (string | null)[][];
  colSpan?: number;
};

const CoverageMultiVectorCell = ({
  value,
  colSpan = 1,
}: CoverageMultiVectorCellProps): React.JSX.Element => (
  <TableCell colSpan={colSpan}>
    <Box display="flex" gap={2} alignItems="center">
      {keyedByText(value, cellsText).map((c) => (
        <Box key={c.key} flex={1} display="flex" gap={2} alignItems="center">
          {keyedByText(c.value, cellText).map((cc) => (
            <Box key={cc.key} flex={1}>
              <DisplayValue value={cc.value} />
            </Box>
          ))}
        </Box>
      ))}
    </Box>
  </TableCell>
);

type CoverageMultiVectorHeaderCellProps = {
  labels: (string | null)[][];
  fontWeight?: HeaderWeight;
  colspan?: number;
};

const CoverageMultiVectorHeaderCell = ({
  labels,
  fontWeight = "bold",
  colspan = 1,
}: CoverageMultiVectorHeaderCellProps): React.JSX.Element => (
  <TableCell colSpan={colspan}>
    <Box display="flex" gap={2}>
      {keyedByText(labels, cellsText).map((c) => (
        <Box key={c.key} flex={1} display="flex" gap={2}>
          {keyedByText(c.value, cellText).map((cc) => (
            <Box key={cc.key} flex={1}>
              <HeaderText fontWeight={fontWeight}>{cc.value}</HeaderText>
            </Box>
          ))}
        </Box>
      ))}
    </Box>
  </TableCell>
);

type CoverageVectorHeaderCellProps = {
  label: string[];
  fontWeight?: HeaderWeight;
  colspan?: number;
};

const CoverageVectorHeaderCell = ({
  label,
  fontWeight = "bold",
  colspan = 1,
}: CoverageVectorHeaderCellProps): React.JSX.Element => (
  <TableCell colSpan={colspan}>
    <Box display="flex" gap={2}>
      {keyedByText(label, cellText).map((c) => (
        <Box key={c.key} flex={1}>
          <HeaderText fontWeight={fontWeight}>{c.value}</HeaderText>
        </Box>
      ))}
    </Box>
  </TableCell>
);

const CoverageHeaderCell = ({
  label,
}: CoverageTitleCellProps): React.JSX.Element => (
  <TableCell>
    <StyledTypography align="center">
      <strong>{label}</strong>
    </StyledTypography>
  </TableCell>
);

type CustomCellProps = {
  children?: ReactNode;
  hasChilds?: boolean;
  isSticky?: boolean;
  sx?: object;
  boxProps?: object;
  align?: "center" | "left" | "right" | "inherit" | "justify";
  colSpan?: number;
};

const stickyStyle = {
  position: "sticky",
  left: 0,
  zIndex: 2,
};

const CustomCell = ({
  children,
  hasChilds = false,
  isSticky = false,
  sx,
  boxProps,
  align,
  colSpan,
  ...rest
}: CustomCellProps): React.JSX.Element => {
  const cellProps: TableCellProps = {
    ...rest,
    padding: "none",
    ...(align ? { align } : {}),
    ...(colSpan ? { colSpan } : {}),
    sx: {
      ...(isSticky ? stickyStyle : {}),
      ...(hasChilds ? {} : { padding: "1rem" }),
      ...sx,
    },
  };
  return (
    <TableCell {...cellProps}>
      {hasChilds ? (
        <Box component={List} dense display="flex" {...boxProps} p={2}>
          {children}
        </Box>
      ) : (
        children
      )}
    </TableCell>
  );
};

export {
  CoverageCell,
  CoverageHeaderCell,
  CoverageMultiVectorCell,
  CoverageMultiVectorHeaderCell,
  CoverageTitleCell,
  CoverageVectorCell,
  CoverageVectorHeaderCell,
  CustomCell,
};
